import { Group } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { memo, useMemo } from "react";
import api, { type ApiResponse } from "../../../api";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../../mantine/notifications/useToast";
import { RegulatoryDutyCategory } from "../../../types/api.types";
import urls from "../../../urls";
import type { Priority, Todo } from "../../../utils/backend-types";
import { TodoStatus } from "../../../utils/backend-types";
import { downloadCsvOrExcelFileForResponseWithContentDisposition } from "../../../utils/files/downloadCsvOrExcelFileForResponseWithContentDisposition";
import { DeleteTodosButton } from "../DeleteTodosButton/DeleteTodosButton";
import { Mode, type TodoUser } from "../Todos.types";
import { getClusterColumn } from "./columns/getClusterColumn";
import { getDueDateColumn } from "./columns/getDueDateColumn";
import { getFulfillForColumn } from "./columns/getFulfillForColumn";
import { getLabelColumn } from "./columns/getLabelColumn";
import { getPersonColumn } from "./columns/getPersonColumn";
import { getPriorityColumn } from "./columns/getPriorityColumn";
import { getProjectColumn } from "./columns/getProjectColumn";
import { getResponsibleColumn } from "./columns/getResponsibleColumn";
import { getStatusColumn } from "./columns/getStatusColumn";
import { getTodoDocumentStatusColumn } from "./columns/getTodoDocumentStatusColumn";
import type { TodoStatusCellProps } from "./TableColumns";

export const OVERDUE_DATE = "-1";

interface ExtraTodoTableFields {
  dueDate: string | null;
}

export type TodoTableTodo = Todo & ExtraTodoTableFields;

interface TodoTableProps extends Pick<TodoStatusCellProps, "onChangeStatus"> {
  variantId: number;
  todos: Array<Todo>;
  users: Array<TodoUser>;
  mode: Mode.Universal | Mode.Onboarding;
  showPersonColumn: boolean;
  showPriorityColumn?: boolean;
  showTodoStatusColumn?: boolean;
  removeTodo: (todo: Todo) => Promise<ApiResponse>;
  removeTodos: (todos: Array<Todo>) => Promise<Array<ApiResponse>>;
  onClickRow: (todo: Todo) => void;
  onChangePriority: (priorityValue: Priority, todoId: number) => void;
  onChangeResponsible: (responsibleId: number | null, todoId: number) => void;
  onChangeTodoStatus: (todoId: number) => void;
  onChangeTodoPerson: (todoId: number, personId: number | null) => void;
  onChangeDueDate: (todoId: number, dueDate: string | null) => void;
  onChangeOverdue: (todoId: number, overdue: boolean) => void;
}

function TodoTable({
  variantId,
  todos,
  users,
  mode,
  showPersonColumn,
  showPriorityColumn,
  showTodoStatusColumn,
  removeTodo,
  removeTodos,
  onChangeStatus,
  onChangePriority,
  onChangeResponsible,
  onChangeTodoStatus,
  onChangeTodoPerson,
  onChangeDueDate,
  onChangeOverdue,
  onClickRow
}: TodoTableProps) {
  const { showToast, showErrorToast } = useToast();

  function handleDownloadSelectedTodoDocuments(selectedTodos: Array<Todo>) {
    const selectedTodoIds = selectedTodos.map((todo) => todo.id);
    if (selectedTodoIds && selectedTodoIds.length > 0) {
      const url = urls.api.downloadSelectedTodoDocuments(variantId);
      const body = {
        todoIds: selectedTodoIds
      };
      api
        .post(url, body)
        .then((response) =>
          downloadCsvOrExcelFileForResponseWithContentDisposition(
            response,
            true
          )
        )
        .catch((error) => {
          showErrorToast(error);
        });
    }
  }

  function handleDeleteTodo(todo: Todo) {
    return removeTodo(todo)
      .then(() =>
        showToast({
          type: "success",
          message: "Aufgabe erfolgreich gelöscht."
        })
      )
      .catch((error) => {
        showErrorToast(error);
      });
  }

  function handleDeleteMultipleTodos(todos: Array<Todo>) {
    return removeTodos(todos)
      .then(() =>
        showToast({
          type: "success",
          message: "Aufgaben erfolgreich gelöscht."
        })
      )
      .catch((error) => {
        showErrorToast(error);
      });
  }

  const todosWithAdjustedOverdueDates = useMemo(
    () =>
      todos.map<TodoTableTodo>((todo) => {
        return {
          ...todo,
          dueDate: todo.overdue ? OVERDUE_DATE : todo.dueDate
        };
      }),
    [todos]
  );

  const { allPersons, allDueDates } = useMemo(() => {
    const uniquePersons = Array.from(
      new Set(
        todosWithAdjustedOverdueDates
          .map((todo) => todo.person)
          .filter((person) => !!person)
      )
    ) as Array<string>;
    const uniqueDueDates = Array.from(
      new Set(
        todosWithAdjustedOverdueDates
          .map((todo) => todo.dueDate)
          .filter((dueDate) => !!dueDate && dueDate !== OVERDUE_DATE)
      )
    ) as Array<string>;
    return {
      allPersons: uniquePersons,
      allDueDates: uniqueDueDates
    };
  }, [todosWithAdjustedOverdueDates]);

  const areSomeTodosNotRelevant = todos.some(
    (todo) => todo.status === TodoStatus.NotRelevant
  );

  const columns: Array<MRT_ColumnDef<TodoTableTodo>> = useMemo(
    () => [
      getLabelColumn(),
      ...(mode === Mode.Universal
        ? [getProjectColumn(), getClusterColumn()]
        : []),
      getFulfillForColumn(),
      ...(showPersonColumn
        ? [getPersonColumn(allPersons, onChangeTodoPerson)]
        : []),
      ...(mode === Mode.Universal
        ? [getDueDateColumn(allDueDates, onChangeDueDate, onChangeOverdue)]
        : []),
      ...(showPriorityColumn ? [getPriorityColumn(onChangePriority)] : []),
      ...(mode === Mode.Onboarding
        ? [getResponsibleColumn(users, onChangeResponsible)]
        : []),
      getStatusColumn(onChangeStatus, areSomeTodosNotRelevant),
      ...(showTodoStatusColumn
        ? [getTodoDocumentStatusColumn(onChangeTodoStatus)]
        : [])
    ],
    [
      allPersons,
      allDueDates,
      mode,
      onChangeDueDate,
      onChangeOverdue,
      onChangePriority,
      onChangeResponsible,
      onChangeStatus,
      onChangeTodoStatus,
      onChangeTodoPerson,
      showPersonColumn,
      showPriorityColumn,
      showTodoStatusColumn,
      users,
      areSomeTodosNotRelevant
    ]
  );

  const mrtTable = useMantineReactTable<TodoTableTodo>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: "Keine Aufgaben entsprechen dem gewählten Filter.",
      initialState: {
        sorting:
          mode === Mode.Universal ? [{ id: "dueDate", desc: false }] : [],
        columnFilters: [{ id: "status", value: [TodoStatus.Open] }]
      },
      hideBorder: true
    }),
    ...getMRTTopToolbarCustomActionsRenderer(),
    // need to overwrite the default renderTopToolbarCustomActions due to special actions
    renderTopToolbarCustomActions: ({ table }) => {
      const selected = table.getSelectedRowModel().rows;

      const { totalDocuments, numberOfTodosWithDocuments } = selected.reduce(
        (result, todo) => ({
          totalDocuments:
            result.totalDocuments + todo.original.todoFiles.length,
          numberOfTodosWithDocuments:
            result.numberOfTodosWithDocuments +
            (todo.original.todoFiles.length > 0 ? 1 : 0)
        }),
        { totalDocuments: 0, numberOfTodosWithDocuments: 0 }
      );

      const documentWording = selected.length === 1 ? "Dokument" : "Dokumente";
      return (
        <Group justify="flex-end" p={0} w="stretch">
          {selected.length > 0 &&
            `${totalDocuments} ${documentWording} (in ${numberOfTodosWithDocuments} von ${selected.length} Aufgaben)`}
          <IconButton
            className="download-selected-documents-button"
            color="primary"
            disabled={selected.length === 0 || totalDocuments === 0}
            icon={IconDownload}
            onClick={() =>
              handleDownloadSelectedTodoDocuments(
                selected.map((todo) => todo.original)
              )
            }
          >
            Dokumente herunterladen
          </IconButton>
          {mode === Mode.Universal && (
            <DeleteTodosButton
              todos={selected.map((todo) => todo.original)}
              onDeleteMultipleTodos={() =>
                handleDeleteMultipleTodos(
                  selected.map((todo) => todo.original)
                ).then(() => table.setRowSelection({}))
              }
              onDeleteTodo={() =>
                handleDeleteTodo(selected[0].original).then(() =>
                  table.setRowSelection({})
                )
              }
            />
          )}
        </Group>
      );
    },
    renderRowActions: undefined,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    autoResetPageIndex: false,
    columnFilterDisplayMode: "popover",
    mantineTableBodyCellProps: ({ row, column }) => ({
      style: { cursor: column.id === "label" ? "pointer" : "default" },
      onClick: () => {
        if (column.id === "label") {
          onClickRow(row.original);
        }
      }
    }),
    enableRowVirtualization: true,
    columns: columns,
    data: todosWithAdjustedOverdueDates
  });

  return <MantineReactTable table={mrtTable} />;
}

const MemoisedTodoTable = memo(TodoTable);

export function isNotChangeable(todo: TodoTableTodo) {
  if (!todo.regulatoryDuty) {
    return false;
  }
  return todo.regulatoryDuty.category !== RegulatoryDutyCategory.Premium;
}

export { MemoisedTodoTable as TodoTable, TodoTableProps };
